import { useState, useMemo } from 'react';
import { Cable, RefreshCw, Plus } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useCablesList } from '../hooks/useCablesList';
import { AddCableModal } from '../components/connectivity/AddCableModal';
import { CablesFilter } from '../components/connectivity/CablesFilter';
import { CablesTable } from '../components/connectivity/CablesTable';

import { filterCables, CableSourceFilter } from '../utils/cables.logic';

interface MiniStatProps {
  label: string;
  value: number;
  sub: string;
  colorClass: string;
}

function MiniStat({ label, value, sub, colorClass }: MiniStatProps) {
  return (
    <div className="relative group bg-slate-900/40 backdrop-blur-md border border-slate-800/50 p-4 rounded-sm flex items-center justify-between shadow-sm overflow-hidden transition-all duration-300 hover:border-accent/50">
      {/* Subtle glow effect */}
      <div className="absolute inset-0 bg-gradient-to-tr from-accent/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      
      <div className="z-10 space-y-1">
        <div className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-bold font-mono text-text-primary tracking-tight">{value}</div>
        <div className="text-[10px] text-slate-500 font-medium">{sub}</div>
      </div>
      <div className="z-10 flex items-center">
        <span className={`w-3.5 h-3.5 rounded-full ${colorClass} shadow-sm animate-pulse`} style={{ animationDuration: '3s' }} />
      </div>
    </div>
  );
}

export default function CablesListPage() {
  const { dcCode } = useParams<{ dcCode: string }>();
  const { userRole } = useAuthStore();
  const canEdit = userRole !== 'viewer';
  
  const { edges, loading, deletingId, fetchCables, handleDelete } = useCablesList(dcCode);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState<CableSourceFilter>('all');
  
  const [showAddModal, setShowAddModal] = useState(false);

  const filteredEdges = useMemo(() => {
    return filterCables(edges, {
      search,
      typeFilter,
      statusFilter,
      sourceFilter,
    });
  }, [edges, search, typeFilter, statusFilter, sourceFilter]);

  if (!dcCode) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800/50 p-6 rounded-sm shadow-sm">
          <h2 className="text-lg font-semibold text-text-primary mb-2">Выберите дата-центр</h2>
          <p className="text-gray-400 text-sm">Пожалуйста, выберите ЦОД в верхнем меню, чтобы просмотреть кабельный журнал.</p>
        </div>
      </div>
    );
  }

  // Extract unique cable types for filter dropdown
  const cableTypes = Array.from(new Set(edges.map(e => e.cable_type))).filter(Boolean);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in font-sans">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800/20 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Cable size={24} className="text-accent" />
            Кабельный журнал (ЦОД: {dcCode})
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Систематизированный реестр всех физических линий коммутации, кабелей и соединений портов.
          </p>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <button
            disabled={!canEdit}
            title={canEdit ? "" : "Добавление кабелей доступно только для инженеров"}
            onClick={() => setShowAddModal(true)}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-accent text-white rounded-sm text-sm font-medium hover:bg-accent-hover transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={14} />
            Добавить кабель
          </button>
          <button
            onClick={fetchCables}
            disabled={loading}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-slate-900/40 border border-slate-800/50 rounded-sm text-sm text-text-secondary hover:bg-slate-800/25 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Обновить
          </button>
        </div>
      </div>

      {/* KPI Cards Summary Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniStat
          label="Всего кабелей"
          value={edges.length}
          sub={edges.some(e => !!e.netbox_id) ? `${edges.filter(e => !!e.netbox_id).length} из NetBox` : 'Зарегистрировано в базе'}
          colorClass="bg-accent/40 border border-accent/60"
        />
        <MiniStat
          label="Активные (Live)"
          value={edges.filter(e => e.status.toLowerCase() === 'live').length}
          sub="Линии под нагрузкой"
          colorClass="bg-green-500/40 border border-green-500/60"
        />
        <MiniStat
          label="Планируемые"
          value={edges.filter(e => e.status.toLowerCase() === 'planned').length}
          sub="В процессе проектирования"
          colorClass="bg-blue-500/40 border border-blue-500/60"
        />
        <MiniStat
          label="Сбойные"
          value={edges.filter(e => e.status.toLowerCase() === 'faulted').length}
          sub="Требуют обслуживания"
          colorClass="bg-red-500/40 border border-red-500/60"
        />
      </div>

      <CablesFilter
        search={search}
        setSearch={setSearch}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        sourceFilter={sourceFilter}
        setSourceFilter={setSourceFilter}
        cableTypes={cableTypes}
      />

      <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800/50 rounded-sm shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw size={24} className="animate-spin text-accent mx-auto mb-2" />
            <p className="text-gray-400 text-sm">Загрузка кабельного журнала...</p>
          </div>
        ) : (
          <CablesTable
            dcCode={dcCode}
            filteredEdges={filteredEdges}
            canEdit={canEdit}
            deletingId={deletingId}
            handleDelete={handleDelete}
          />
        )}
      </div>

      <AddCableModal
        dcCode={dcCode}
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCableAdded={fetchCables}
      />
    </div>
  );
}
